<template>
  <v-card class="rb">
    <header class="rb__head">
      <div class="rb__head-text">
        <p class="rb__eyebrow">Contabilidad</p>
        <h2 class="rb__title">Detalle del reembolso</h2>
        <!-- El consolidado se nombra por su CONS-, no por el control F&D
             viejo (LN…) que trae invoiceNumber; la individual, por su
             número de factura y su IND-. -->
        <p class="rb__scope">
          <template v-if="item.consolidated">
            Consolidado
            <strong>{{ item.internalNumber || item.invoiceNumber }}</strong>
          </template>
          <template v-else>
            Factura <strong>{{ item.invoiceNumber }}</strong>
            <span v-if="item.internalNumber"> · {{ item.internalNumber }}</span>
          </template>
          · CIF USD {{ money(item.cif) }}
        </p>
      </div>
      <button type="button" class="rb__close" title="Cerrar" @click="close">
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="rb__body">
      <p v-if="loading" class="rb__help">Cargando detalle…</p>

      <!-- Conceptos adelantados -->
      <ul v-else class="rb__list">
        <li v-for="line in lines" :key="line.id" class="rb__line">
          <div class="rb__line-head">
            <span class="rb__concept">
              {{ line.concept.name }}
              <span v-if="isTax(line)" class="rb__auto">
                automático
              </span>
            </span>
            <input
              type="text"
              class="rb__amount"
              inputmode="decimal"
              placeholder="0.00"
              :value="line.amount"
              :readonly="isTax(line)"
              :class="{ 'rb__amount--locked': isTax(line) }"
              @change="saveAmount(line, $event.target.value)"
            />
            <!-- Eliminar se lleva la línea y sus comprobantes: se confirma
                 en sitio, sin diálogo nativo. -->
            <span v-if="confirming === line.id" class="rb__confirm">
              ¿Eliminar?
              <button
                class="rb__confirm-btn rb__confirm-btn--yes"
                @click="remove(line)"
              >
                Sí
              </button>
              <button class="rb__confirm-btn" @click="confirming = null">
                No
              </button>
            </span>
            <button
              v-else-if="!isTax(line)"
              class="rb__icon-btn rb__icon-btn--danger"
              title="Eliminar este concepto y sus comprobantes"
              @click="confirming = line.id"
            >
              <v-icon small>delete_outline</v-icon>
            </button>
            <span
              v-else
              class="rb__lock"
              title="Se toma del módulo de Pago de Impuestos"
            >
              <v-icon small>lock</v-icon>
            </span>
          </div>

          <input
            type="text"
            class="rb__notes"
            placeholder="Detalle: a quién se pagó, número de recibo, lo que haga falta…"
            :value="line.notes"
            :readonly="isTax(line)"
            @change="saveNotes(line, $event.target.value)"
          />

          <!-- Documentos de este concepto. El pedido los separa en dos: el
               comprobante del pago que se adelantó y la solicitud de reembolso
               que se le presenta al cliente. Cada uno con su botón. -->
          <div v-for="kind in KINDS" :key="kind.code" class="rb__docs">
            <span class="rb__doc-kind">{{ kind.label }}</span>
            <span
              v-for="doc in docsOf(line, kind.code)"
              :key="doc.id"
              class="rb__doc-item"
            >
              <a
                :href="fullPath(doc.filePath)"
                target="_blank"
                rel="noopener"
                class="rb__doc"
                :title="doc.originalName || doc.filePath"
              >
                <v-icon x-small>insert_drive_file</v-icon>
                {{ doc.originalName || doc.filePath }}
              </a>
              <!-- Eliminar confirma en sitio: el archivo se borra del disco. -->
              <template v-if="confirmingDoc === doc.id">
                <span class="rb__confirm">
                  ¿Eliminar?
                  <button
                    class="rb__confirm-btn rb__confirm-btn--yes"
                    :disabled="deletingDoc"
                    @click="deleteDoc(doc, false)"
                  >
                    Sí
                  </button>
                  <button class="rb__confirm-btn" @click="confirmingDoc = null">
                    No
                  </button>
                </span>
              </template>
              <button
                v-else-if="canDelete(doc)"
                class="rb__doc-remove"
                title="Eliminar este documento"
                @click="confirmingDoc = doc.id"
              >
                <v-icon x-small>close</v-icon>
              </button>
              <span
                v-else
                class="rb__doc-locked"
                title="Es el comprobante del pago de impuestos: se administra en Pago de Impuestos"
              >
                <v-icon x-small>lock</v-icon>
              </span>
            </span>
            <span v-if="!docsOf(line, kind.code).length" class="rb__no-doc">
              {{ kind.empty }}
            </span>
            <button
              class="rb__icon-btn"
              :title="kind.attach"
              @click="pick(line, kind.code)"
            >
              <v-icon small>attach_file</v-icon>
            </button>
          </div>
        </li>
      </ul>

      <p v-if="!loading && !lines.length" class="rb__help">
        Este trámite todavía no tiene pagos adelantados.
      </p>

      <!-- Agregar concepto -->
      <div class="rb__add">
        <v-select
          v-model="nuevo.conceptId"
          :items="selectable"
          item-text="name"
          item-value="id"
          placeholder="Agregar concepto…"
          no-data-text="Sin conceptos disponibles"
          solo
          flat
          hide-details
          class="rb__select"
        ></v-select>
        <input
          v-model="nuevo.amount"
          type="text"
          inputmode="decimal"
          class="rb__amount"
          placeholder="0.00"
        />
        <button
          class="rb__btn rb__btn--add"
          :disabled="!nuevo.conceptId || adding"
          @click="add"
        >
          <v-icon small class="rb__btn-icon">add</v-icon>
          {{ adding ? 'Agregando…' : 'Agregar' }}
        </button>
      </div>

      <!-- El catálogo arrancó con los conceptos del pedido, pero no está
           cerrado: lo que aparezca con el tiempo se agrega desde aquí, sin
           desplegar. Queda disponible para todos los trámites. -->
      <div v-if="creating" class="rb__new">
        <input
          ref="conceptName"
          v-model="nuevoConcepto"
          type="text"
          class="rb__new-input"
          placeholder="Nombre del concepto nuevo…"
          @keyup.enter="createConcept"
        />
        <button
          class="rb__btn rb__btn--add"
          :disabled="!nuevoConcepto.trim() || savingConcept"
          @click="createConcept"
        >
          <v-icon small class="rb__btn-icon">check</v-icon>
          {{ savingConcept ? 'Creando…' : 'Crear' }}
        </button>
        <button class="rb__btn rb__btn--ghost" @click="cancelConcept">
          Cancelar
        </button>
      </div>
      <button v-else class="rb__new-toggle" @click="startConcept">
        <v-icon small class="rb__btn-icon">playlist_add</v-icon>
        Crear un concepto nuevo
      </button>

      <div class="rb__total">
        <span class="rb__total-label">Total a reembolsar</span>
        <strong class="rb__total-value">B/. {{ money(total) }}</strong>
      </div>

      <!-- Factura de reembolso del trámite: la que genera Alegra. Sin monto,
           porque el total ya lo suman los conceptos (impuesto incluido). Al
           adjuntarla se sella la fecha y el reembolso entra al Estado de
           Cuenta. -->
      <section class="rb__invoice">
        <div class="rb__invoice-head">
          <span class="rb__invoice-title">Factura de reembolso</span>
          <span
            v-if="
              item.reimbursementInvoiceDate && item.reimbursementInvoiceNumber
            "
            class="rb__invoice-date"
          >
            Facturado el {{ dateOf(item.reimbursementInvoiceDate) }}
          </span>
          <span
            v-else-if="item.reimbursementInvoiceDate"
            class="rb__invoice-date rb__invoice-date--pending"
          >
            Facturado el {{ dateOf(item.reimbursementInvoiceDate) }} · falta el
            N° de factura Alegra
          </span>
          <span v-else class="rb__invoice-date rb__invoice-date--pending">
            Pendiente: adjunta la factura de Alegra
            <template v-if="!item.reimbursementInvoiceNumber">
              y escribe su número
            </template>
          </span>
        </div>

        <!-- N° y fecha de la factura de Alegra. El estado de cuenta lista,
             por trámite, el N° Alegra, la fecha y el monto; sin el N° no se
             puede emitir. La fecha la pone el adjunto al subir la factura y
             aquí solo se corrige: es la que define el mes del estado de
             cuenta. Se guardan al salir del campo, como el # Control. -->
        <div class="rb__alegra">
          <label class="rb__alegra-field">
            <span class="rb__alegra-label">N° factura Alegra</span>
            <input
              type="text"
              class="rb__alegra-input rb__alegra-input--number"
              placeholder="N° de factura Alegra"
              maxlength="40"
              :value="alegraValue('reimbursementInvoiceNumber')"
              :readonly="alegra.reimbursementInvoiceNumber.saving"
              :class="{
                'rb__alegra-input--error':
                  alegra.reimbursementInvoiceNumber.error,
              }"
              @input="
                onAlegraInput('reimbursementInvoiceNumber', $event.target.value)
              "
              @keyup.enter="$event.target.blur()"
              @keyup.esc="cancelAlegra('reimbursementInvoiceNumber', $event)"
              @blur="saveAlegra('reimbursementInvoiceNumber')"
            />
          </label>
          <label v-if="item.reimbursementInvoiceDate" class="rb__alegra-field">
            <span class="rb__alegra-label">Fecha de factura</span>
            <input
              type="date"
              class="rb__alegra-input rb__alegra-input--date"
              :max="today"
              :value="alegraValue('reimbursementInvoiceDate')"
              :readonly="alegra.reimbursementInvoiceDate.saving"
              :class="{
                'rb__alegra-input--error':
                  alegra.reimbursementInvoiceDate.error,
              }"
              @change="
                saveAlegraDate('reimbursementInvoiceDate', $event.target.value)
              "
            />
          </label>
          <span v-if="alegraSaving" class="rb__alegra-status">Guardando…</span>
          <span v-else-if="alegraError" class="rb__alegra-error">
            {{ alegraError }}
          </span>
          <!-- El N° repetido solo se avisa: casi siempre es un error de
               tecleo, pero una misma factura de Alegra podría cubrir dos
               trámites, así que no se bloquea nada. -->
          <span
            v-else-if="alegraDuplicateText"
            class="rb__alegra-warn"
            :title="alegraDuplicateTitle"
          >
            {{ alegraDuplicateText }}
          </span>
        </div>
        <p v-if="item.reimbursementInvoiceDate" class="rb__help">
          Es la fecha que define el mes del estado de cuenta.
        </p>

        <div class="rb__docs">
          <span v-for="doc in invoiceDocs" :key="doc.id" class="rb__doc-item">
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="rb__doc"
              :title="doc.originalName || doc.filePath"
            >
              <v-icon x-small>insert_drive_file</v-icon>
              {{ doc.originalName || doc.filePath }}
            </a>
            <!-- Si se borra la última, el backend quita la fecha de factura y
                 el reembolso sale del Estado de Cuenta hasta volver a
                 adjuntarla. -->
            <template v-if="confirmingDoc === doc.id">
              <span class="rb__confirm">
                ¿Eliminar?
                <button
                  class="rb__confirm-btn rb__confirm-btn--yes"
                  :disabled="deletingDoc"
                  @click="deleteDoc(doc, true)"
                >
                  Sí
                </button>
                <button class="rb__confirm-btn" @click="confirmingDoc = null">
                  No
                </button>
              </span>
            </template>
            <button
              v-else
              class="rb__doc-remove"
              title="Eliminar esta factura"
              @click="confirmingDoc = doc.id"
            >
              <v-icon x-small>close</v-icon>
            </button>
          </span>
          <span v-if="!invoiceDocs.length" class="rb__no-doc">
            Sin factura adjunta
          </span>
          <button
            class="rb__icon-btn"
            title="Adjuntar la factura de reembolso"
            @click="pick(null)"
          >
            <v-icon small>attach_file</v-icon>
          </button>
        </div>
        <p class="rb__help">
          Es la factura total del reembolso: no lleva monto porque el total ya
          suma todos los conceptos, incluido el impuesto.
        </p>
      </section>

      <p v-if="error" class="rb__error">{{ error }}</p>

      <input
        ref="file"
        type="file"
        multiple
        style="display: none"
        @change="upload"
      />
    </div>

    <footer class="rb__foot">
      <!-- Viene marcada solo mientras no haya factura de reembolso: ese es el
           aviso que le importa al cliente. Agregar o reemplazar después no
           reenvía nada, salvo que se marque. -->
      <label class="rb__notify">
        <input v-model="notifyClient" type="checkbox" />
        Avisar al cliente por correo (FACTURACIÓN DE REEMBOLSO)
      </label>
      <button type="button" class="rb__btn rb__btn--ghost" @click="close">
        Cerrar
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    lines: [],
    concepts: [],
    loading: true,
    adding: false,
    error: '',
    nuevo: { conceptId: null, amount: '' },
    // Alta de un concepto que no está en el catálogo.
    creating: false,
    nuevoConcepto: '',
    savingConcept: false,
    notifyClient: false,
    // Línea a la que se adjuntará el archivo que se está eligiendo, o
    // 'INVOICE' cuando es la factura de reembolso del trámite.
    target: null,
    // Tipo del documento que se va a subir a una línea.
    kind: 'COMPROBANTE',
    // Facturas de reembolso adjuntas al trámite (documentos sin línea).
    invoiceDocs: [],
    // Todos los documentos de reembolso del trámite, por id. Es la fuente del
    // tipo (comprobante / solicitud) y dice qué se puede borrar desde aquí.
    docsById: {},
    // Línea cuya eliminación se está confirmando.
    confirming: null,
    // Documento cuya eliminación se está confirmando.
    confirmingDoc: null,
    deletingDoc: false,
    // Si se tocó algo, al cerrar hay que refrescar el listado de atrás.
    changed: false,
    // Borrador y estado de guardado del N° y la fecha de la factura Alegra.
    alegra: {
      reimbursementInvoiceNumber: { draft: null, saving: false, error: '' },
      reimbursementInvoiceDate: { draft: null, saving: false, error: '' },
    },
    // Otros trámites que ya tienen este mismo N° de factura Alegra.
    alegraDuplicates: [],
    KINDS: [
      {
        code: 'COMPROBANTE',
        label: 'Comprobante',
        empty: 'Sin comprobante de pago',
        attach: 'Adjuntar el comprobante del pago que se adelantó',
      },
      {
        code: 'SOLICITUD',
        label: 'Solicitud',
        empty: 'Sin solicitud de reembolso',
        attach: 'Adjuntar la solicitud de reembolso',
      },
    ],
  }),
  computed: {
    total() {
      return this.lines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0)
    },
    today() {
      return moment().format('YYYY-MM-DD')
    },
    alegraSaving() {
      return Object.keys(this.alegra).some(f => this.alegra[f].saving)
    },
    alegraError() {
      const field = Object.keys(this.alegra).find(f => this.alegra[f].error)
      return field ? this.alegra[field].error : ''
    },
    // Se nombran dos trámites y el resto se cuenta: la lista completa va en
    // el title, para no romper la línea de campos.
    alegraDuplicateText() {
      const nombres = this.alegraDuplicateNames
      if (!nombres.length) return ''
      const resto = nombres.length - 2
      const visibles = nombres.slice(0, 2).join(', ')
      return resto > 0
        ? `Este N° ya está en ${visibles} y ${resto} más`
        : `Este N° ya está en ${visibles}`
    },
    alegraDuplicateTitle() {
      return this.alegraDuplicateNames.join(', ')
    },
    alegraDuplicateNames() {
      return this.alegraDuplicates.map(
        d => d.internalNumber || d.invoiceNumber || `#${d.id}`
      )
    },
    // El pago de impuestos no se agrega a mano y un concepto ya registrado no
    // se repite: se edita el que está.
    selectable() {
      const usados = this.lines.map(l => l.concept.id)
      return this.concepts.filter(
        c => c.code !== 'TAX' && usados.indexOf(c.id) === -1
      )
    },
  },
  created() {
    // Este es el aviso que importa mientras no haya factura de reembolso; con
    // fecha ya sellada, subir o reemplazar archivos no debe avisar de nuevo.
    this.notifyClient = !this.item.reimbursementInvoiceDate
    this.load()
    this.checkAlegraDuplicates()
  },
  methods: {
    fullPath: getFullPath,
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    money(value) {
      const n = parseFloat(value)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    isTax(line) {
      return line.concept && line.concept.code === 'TAX'
    },
    // === N° y fecha de la factura Alegra ===
    alegraValue(field) {
      const st = this.alegra[field]
      return st.draft !== null ? st.draft : this.item[field] || ''
    },
    onAlegraInput(field, value) {
      this.alegra[field] = { draft: value, saving: false, error: '' }
    },
    // Escape descarta lo escrito y suelta el foco; el blur que sigue no
    // guarda nada porque ya no hay borrador.
    cancelAlegra(field, event) {
      this.alegra[field] = { draft: null, saving: false, error: '' }
      event.target.value = this.item[field] || ''
      event.target.blur()
    },
    // El selector de fecha entrega el valor completo en change: se guarda ahí.
    saveAlegraDate(field, value) {
      this.onAlegraInput(field, value)
      this.saveAlegra(field)
    },
    // Guarda en el TRÁMITE (item.id es la fila padre en un consolidado). El
    // N° puede vaciarse; la fecha no: sin fecha no hay factura, y quitarla es
    // eliminar el adjunto.
    saveAlegra(field) {
      const st = this.alegra[field]
      if (st.draft === null || st.saving) return
      const next = String(st.draft).trim()
      const current = this.item[field] || ''
      if (next === current) {
        this.alegra[field] = { draft: null, saving: false, error: '' }
        return
      }
      if (field === 'reimbursementInvoiceDate' && next === '') {
        this.alegra[field] = {
          draft: null,
          saving: false,
          error: 'La fecha no puede quedar vacía.',
        }
        return
      }
      this.alegra[field] = { draft: next, saving: true, error: '' }
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: this.item.id,
          [field]: next,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.alegra[field] = {
              draft: next,
              saving: false,
              error: this.errorText(response, 'No se pudo guardar el dato.'),
            }
            return
          }
          // item es la misma fila de la lista: se actualiza sola.
          this.$set(this.item, field, next === '' ? null : next)
          this.alegra[field] = { draft: null, saving: false, error: '' }
          this.changed = true
          this.$emit('changed')
          if (field === 'reimbursementInvoiceNumber') {
            this.checkAlegraDuplicates()
          }
        })
        .catch(() => {
          this.alegra[field] = {
            draft: next,
            saving: false,
            error: 'No se pudo guardar el dato.',
          }
        })
    },
    // Avisa si el N° ya está en otro trámite. Es secundario: si la consulta
    // falla no se estorba al usuario, el guardado ya ocurrió.
    checkAlegraDuplicates() {
      const value = this.item.reimbursementInvoiceNumber || ''
      if (!value) {
        this.alegraDuplicates = []
        return
      }
      this.$store
        .dispatch('invoices/checkAlegraNumber', {
          field: 'reimbursementInvoiceNumber',
          value,
          invoiceId: this.item.id,
        })
        // La acción del store ya devuelve el arreglo de trámites (o [] si la
        // consulta falló), no la respuesta completa.
        .then(duplicates => {
          this.alegraDuplicates = Array.isArray(duplicates) ? duplicates : []
        })
        .catch(() => {
          this.alegraDuplicates = []
        })
    },
    // El backend responde { error: 'mensaje' }; se cubre también `message`.
    errorText(response, fallback) {
      const data = response && response.data
      if (!data) return fallback
      if (typeof data.error === 'string' && data.error) return data.error
      if (typeof data.message === 'string' && data.message) return data.message
      return fallback
    },
    load() {
      this.loading = true
      Promise.all([
        this.$store.dispatch('invoices/getReimbursements', {
          ids: String(this.item.id),
        }),
        this.$store.dispatch('invoices/getReimbursementConcepts'),
        this.$store.dispatch('invoices/getDocuments', {
          ids: String(this.item.id),
          step: 'REIMBURSEMENT',
        }),
      ])
        .then(([lines, concepts, docs]) => {
          this.lines = lines || []
          this.concepts = concepts || []
          // Los comprobantes de cada concepto van con su línea; los que no
          // tienen línea son la factura de reembolso del trámite.
          this.invoiceDocs = (docs || []).filter(d => !d.reimbursementId)
          const byId = {}
          ;(docs || []).forEach(d => {
            byId[d.id] = d
          })
          this.docsById = byId
        })
        .finally(() => {
          this.loading = false
        })
    },
    add() {
      this.error = ''
      this.adding = true
      this.$store
        .dispatch('invoices/saveReimbursement', {
          invoiceId: this.item.id,
          conceptId: this.nuevo.conceptId,
          amount: this.nuevo.amount === '' ? null : this.nuevo.amount,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo agregar el concepto.'
            return
          }
          this.nuevo = { conceptId: null, amount: '' }
          this.load()
          this.changed = true
          this.$emit('changed')
        })
        .finally(() => {
          this.adding = false
        })
    },
    startConcept() {
      this.creating = true
      this.$nextTick(() => {
        if (this.$refs.conceptName) this.$refs.conceptName.focus()
      })
    },
    cancelConcept() {
      this.creating = false
      this.nuevoConcepto = ''
    },
    createConcept() {
      const name = this.nuevoConcepto.trim()
      if (!name || this.savingConcept) return
      this.error = ''
      this.savingConcept = true
      this.$store
        .dispatch('invoices/saveReimbursementConcept', { name })
        .then(concept => {
          if (!concept || !concept.id) {
            this.error = 'No se pudo crear el concepto.'
            return null
          }
          this.creating = false
          this.nuevoConcepto = ''
          return this.$store
            .dispatch('invoices/getReimbursementConcepts')
            .then(concepts => {
              this.concepts = concepts || []
              // Se crea para usarlo ya: queda elegido en el selector.
              this.nuevo.conceptId = concept.id
            })
        })
        .catch(() => {
          this.error = 'No se pudo crear el concepto.'
        })
        .finally(() => {
          this.savingConcept = false
        })
    },
    saveAmount(line, raw) {
      const parsed = raw === '' ? null : parseFloat(raw)
      if (raw !== '' && Number.isNaN(parsed)) {
        this.error = 'El monto debe ser un número.'
        return
      }
      this.error = ''
      const previous = line.amount
      line.amount = parsed
      this.$store
        .dispatch('invoices/saveReimbursement', { id: line.id, amount: raw })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            line.amount = previous
            this.error = 'No se pudo guardar el monto.'
            return
          }
          this.changed = true
          this.$emit('changed')
        })
    },
    saveNotes(line, raw) {
      const previous = line.notes
      line.notes = raw
      this.$store
        .dispatch('invoices/saveReimbursement', { id: line.id, notes: raw })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            line.notes = previous
            this.error = 'No se pudo guardar el detalle.'
          }
        })
    },
    remove(line) {
      this.confirming = null
      this.$store
        .dispatch('invoices/deleteReimbursement', { id: line.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo eliminar el concepto.'
            return
          }
          this.load()
          this.changed = true
          this.$emit('changed')
        })
    },
    // El tipo viaja en `comment` del documento. Los documentos viejos, sin
    // valor, son comprobantes. Los comprobantes del pago de impuestos vienen
    // enlazados desde Pago de Impuestos y no están en el mapa: se muestran,
    // pero se administran allá.
    kindOf(doc) {
      const known = this.docsById[doc.id]
      return known && known.comment === 'SOLICITUD'
        ? 'SOLICITUD'
        : 'COMPROBANTE'
    },
    docsOf(line, kind) {
      return (line.documents || []).filter(d => this.kindOf(d) === kind)
    },
    canDelete(doc) {
      return Boolean(this.docsById[doc.id])
    },
    deleteDoc(doc, esFactura) {
      this.deletingDoc = true
      this.error = ''
      this.$store
        .dispatch('invoices/deleteDocument', { id: doc.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudo eliminar el documento.'
            return
          }
          this.changed = true
          // Sin factura adjunta el backend quita la fecha: se refleja aquí
          // sin esperar la recarga, igual que al subirla.
          if (esFactura && this.invoiceDocs.length <= 1) {
            this.item.reimbursementInvoiceDate = null
            this.notifyClient = true
          }
          this.load()
          this.$emit('changed')
        })
        .catch(() => {
          this.error = 'No se pudo eliminar el documento.'
        })
        .finally(() => {
          this.deletingDoc = false
          this.confirmingDoc = null
        })
    },
    // Cerrar recarga el listado solo si se tocó algo: semáforo, fecha y
    // totales de la fila pueden haber cambiado.
    close() {
      this.$emit(this.changed ? 'saved' : 'close-reimbursement')
    },
    pick(line, kind) {
      this.target = line || 'INVOICE'
      this.kind = kind || 'COMPROBANTE'
      this.$refs.file.click()
    },
    upload(e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (!files.length || !this.target) return
      const esFactura = this.target === 'INVOICE'
      this.error = ''
      const params = {
        invoiceId: this.item.id,
        files,
        sendEmails: this.notifyClient ? 'true' : 'false',
      }
      // Sin línea, el backend lo toma como la factura de reembolso del
      // trámite y sella la fecha la primera vez. Con línea, va con su tipo.
      if (!esFactura) {
        params.reimbursementId = this.target.id
        params.kind = this.kind
      }
      this.$store
        .dispatch('invoices/uploadReimbursementDocs', params)
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.error = 'No se pudieron subir los documentos.'
            return
          }
          this.changed = true
          if (esFactura && !this.item.reimbursementInvoiceDate) {
            // La lista trae la fecha real al recargar; esto evita el parpadeo.
            this.item.reimbursementInvoiceDate = moment().format('YYYY-MM-DD')
          }
          // Ya avisado: lo que se suba después es agregar o reemplazar.
          this.notifyClient = false
          this.load()
          this.$emit('changed')
        })
        .finally(() => {
          this.target = null
        })
    },
  },
}
</script>

<style scoped>
.rb {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.rb__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #b45309;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.rb__head-text {
  flex: 1;
  min-width: 0;
}
.rb__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.rb__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}
.rb__scope {
  margin: 4px 0 0;
  font-size: 14px;
  color: #64748b;
}
.rb__scope strong {
  color: #254f87;
}
.rb__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
}
.rb__close:hover {
  background: #f1f5f9;
}
.rb__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.rb__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.rb__help {
  margin: 6px 0;
  font-size: 13.5px;
  color: #94a3b8;
}
.rb__error {
  margin: 8px 0 0;
  font-size: 13.5px;
  color: #b91c1c;
}

/* === Conceptos === */
.rb__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.rb__line {
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.rb__line-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rb__concept {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: #0f172a;
}
/* El pago de impuestos se refleja del otro módulo: se marca para que nadie lo
   intente corregir aquí y queden dos cifras del mismo pago. */
.rb__auto {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.rb__amount {
  flex: 0 0 auto;
  width: 110px;
  text-align: right;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  background: #ffffff;
}
.rb__amount:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.rb__amount--locked {
  background: #f1f5f9;
  color: #64748b;
}
.rb__notes {
  width: 100%;
  margin-top: 6px;
  height: 30px;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13.5px;
  color: #334155;
  background: #f8fafc;
}
.rb__notes:hover {
  border-color: #e2e8f0;
}
.rb__notes:focus {
  outline: none;
  border-color: #254f87;
  background: #ffffff;
}
.rb__notes::placeholder {
  color: #cbd5e1;
}
.rb__docs {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.rb__doc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 10px;
  background: #eef2f7;
  color: #254f87;
  font-size: 12.5px;
  text-decoration: none;
}
.rb__doc:hover {
  background: #e2e8f0;
}
.rb__no-doc {
  font-size: 12.5px;
  color: #cbd5e1;
}
/* Cada línea separa comprobante y solicitud; la etiqueta abre la fila. */
.rb__doc-kind {
  flex: 0 0 82px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}
.rb__doc-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
}
.rb__doc-remove {
  border: none;
  background: transparent;
  border-radius: 4px;
  padding: 2px;
  cursor: pointer;
  line-height: 1;
}
.rb__doc-remove:hover {
  background: #fee2e2;
}
.rb__doc-remove .v-icon {
  color: #b91c1c !important;
}
.rb__doc-locked {
  display: inline-flex;
  padding: 2px;
  line-height: 1;
}
.rb__doc-locked .v-icon {
  color: #cbd5e1 !important;
}
.rb__icon-btn {
  border: none;
  background: transparent;
  border-radius: 5px;
  padding: 3px;
  cursor: pointer;
  line-height: 1;
}
.rb__icon-btn:hover {
  background: #f1f5f9;
}
.rb__icon-btn .v-icon {
  color: #64748b !important;
}
.rb__icon-btn--danger:hover {
  background: #fee2e2;
}
.rb__icon-btn--danger .v-icon {
  color: #b91c1c !important;
}
.rb__lock .v-icon {
  color: #cbd5e1 !important;
}

/* === Agregar === */
.rb__add {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
}
.rb__select {
  flex: 1;
}
.rb__new {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.rb__new-input {
  flex: 1;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
}
.rb__new-input:focus {
  outline: none;
  border-color: #254f87;
}
.rb__new-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: #254f87;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.rb__new-toggle:hover {
  text-decoration: underline;
}
.rb__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.rb__btn--add {
  background: #254f87;
  border-color: #254f87;
  color: #ffffff;
}
.rb__btn--add .rb__btn-icon {
  color: #ffffff !important;
}
.rb__btn--add:hover:not(:disabled) {
  background: #1a3a63;
}
.rb__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
  height: 38px;
}
.rb__btn--ghost:hover {
  background: #f1f5f9;
}
.rb__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.rb__total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 16px 0 4px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.rb__total-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #92400e;
}
.rb__total-value {
  font-size: 20px;
  color: #78350f;
  font-variant-numeric: tabular-nums;
}

/* === Pie === */
.rb__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.rb__notify {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  color: #475569;
  cursor: pointer;
}
/* === Factura de reembolso (Alegra) === */
.rb__invoice {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #254f87;
  border-radius: 8px;
  background: #f8fafc;
}
.rb__invoice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}
.rb__invoice-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}
.rb__invoice-date {
  font-size: 12.5px;
  font-weight: 600;
  color: #15803d;
}
.rb__invoice-date--pending {
  color: #991b1b;
}
/* N° y fecha de la factura Alegra, en línea dentro del bloque. */
.rb__alegra {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin: 4px 0 8px;
}
.rb__alegra-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.rb__alegra-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.rb__alegra-input {
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  color: #0f172a;
  background: #ffffff;
}
.rb__alegra-input--number {
  width: 200px;
  font-weight: 600;
}
.rb__alegra-input--date {
  width: 160px;
  font-variant-numeric: tabular-nums;
}
.rb__alegra-input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12);
}
.rb__alegra-input--error {
  border-color: #dc2626;
}
.rb__alegra-status {
  padding-bottom: 8px;
  font-size: 12.5px;
  color: #94a3b8;
}
.rb__alegra-error {
  padding-bottom: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #b91c1c;
}
/* Ámbar, no rojo: el N° repetido es un aviso, no un error; no impide
   guardar ni emitir el estado de cuenta. */
.rb__alegra-warn {
  padding-bottom: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: #92400e;
  cursor: help;
}
/* Confirmación en sitio al eliminar una línea. */
.rb__confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #991b1b;
  white-space: nowrap;
}
.rb__confirm-btn {
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.rb__confirm-btn--yes {
  border-color: #dc2626;
  background: #dc2626;
  color: #ffffff;
}
</style>
